import React, { useState, useEffect } from 'react';
import {
    Drawer,
    Form,
    Input,
    Button,
    Space,
    message,
    Divider,
    Upload,
    Tabs,
    Typography,
    Alert,
    Table,
    Collapse,
    Tag,
    Empty
} from 'antd';
import {
    InboxOutlined,
    FileTextOutlined,
    EditOutlined,
    CloudDownloadOutlined,
    SearchOutlined,
    PlusOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { campaignService, campaignParticipantService } from '../services/api';
import * as XLSX from 'xlsx';

const { Dragger } = Upload;
const { TextArea } = Input;
const { Text, Title } = Typography;
const { Panel } = Collapse;

const CampaignContactsDrawer = ({ visible, onClose, campaign, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('list'); // Default to list
    const [loadMode, setLoadMode] = useState('csv'); // internal for upload tab
    const [csvPhones, setCsvPhones] = useState([]);
    const [groupedParticipants, setGroupedParticipants] = useState([]);

    const fetchData = async () => {
        if (!campaign?.id || !visible) return;
        setLoading(true);
        try {
            const res = await campaignParticipantService.list(campaign.id);
            if (res.status === 'success') {
                setGroupedParticipants(res.data);
            }
        } catch (error) {
            message.error('Error al cargar contactos');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (visible && campaign?.id) {
            fetchData();
        }
    }, [visible, campaign?.id]);

    const handleFileLoad = (file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });
                const firstSheetName = workbook.SheetNames[0];
                const worksheet = workbook.Sheets[firstSheetName];
                const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

                if (jsonData.length === 0) {
                    message.error("El archivo está vacío");
                    return;
                }

                const processed = [];
                let validCount = 0;

                jsonData.forEach(row => {
                    // Find Phone Column (Case Insensitive)
                    const keys = Object.keys(row);
                    const phoneKey = keys.find(k => /tel[eé]fono|celular|phone|movil/i.test(k));

                    if (phoneKey && row[phoneKey]) {
                        const phone = String(row[phoneKey]).trim();
                        if (phone) {
                            // Extract Metadata (Everything else)
                            const metadata = { ...row };
                            delete metadata[phoneKey];

                            // Remove empty metadata keys
                            Object.keys(metadata).forEach(key => {
                                if (metadata[key] === "" || metadata[key] === null || metadata[key] === undefined) {
                                    delete metadata[key];
                                }
                            });

                            processed.push({
                                phone: phone,
                                metadata: metadata
                            });
                            validCount++;
                        }
                    } else if (keys.length === 1 && !phoneKey) {
                        // Fallback: If only 1 column, assume it's phone even if header doesn't match
                        const val = Object.values(row)[0];
                        if (val) {
                            processed.push({ phone: String(val).trim(), metadata: {} });
                            validCount++;
                        }
                    }
                });

                setCsvPhones(processed);
                if (validCount > 0) {
                    message.success(`${validCount} registros válidos detectados.`);
                } else {
                    message.warning("No se encontró columna 'Telefono' válida.");
                }
            } catch (error) {
                console.error("Excel Parse Error", error);
                message.error("Error al procesar el archivo. Verifique el formato.");
            }
        };
        reader.readAsArrayBuffer(file);
        return false; // Prevent upload
    };

    const downloadErrorsReport = (report, campaignName, batchTag) => {
        if (!report || report.length === 0) return;

        const headers = ['Teléfono', 'Estado', 'Razón'];
        const csvContent = [
            headers.join(','),
            ...report.map(row => `"${row.phone}","${row.status}","${row.reason}"`)
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `Errores_Carga_${campaignName}_${batchTag}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleSubmitLoad = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            let participantsToAssign = [];

            if (loadMode === 'csv') {
                participantsToAssign = csvPhones;
                if (participantsToAssign.length === 0) {
                    message.error('Por favor cargue un archivo válido con registros');
                    setLoading(false);
                    return;
                }
            } else {
                const manualText = values.manual_phones || '';
                const manualPhones = manualText
                    .split(/\r?\n|,|;/)
                    .map(p => p.trim())
                    .filter(p => p.length > 0);

                // Convert manual phones to structure
                participantsToAssign = manualPhones.map(p => ({ phone: p, metadata: {} }));

                if (participantsToAssign.length === 0) {
                    message.error('Por favor ingrese al menos un número telefónico');
                    setLoading(false);
                    return;
                }
            }

            const res = await campaignService.assignParticipants({
                campaign_id: campaign.id,
                participants: participantsToAssign, // Send participants array
                batch_tag: values.batch_tag
            });

            if (res.status === 'success') {
                const { summary, report } = res.data;
                const { created, duplicates, invalid } = summary;

                message.success(`Carga completada: ${created} nuevos registros.`);

                if (report && report.length > 0) {
                    message.warning(`${duplicates} duplicados y ${invalid} inválidos omitidos. Descargando reporte de errores...`);
                    downloadErrorsReport(report, campaign?.name, values.batch_tag);
                }

                // Cleanup UI
                form.resetFields();
                setCsvPhones([]);

                // Refresh list and switch tab
                await fetchData();
                setActiveTab('list');

                if (onSuccess) onSuccess();
            }
        } catch (error) {
            const errorMsg = error.response?.data?.message || 'Error al asignar participantes';
            message.error(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleExportBatch = (batchTag) => {
        campaignParticipantService.export(campaign.id, batchTag);
    };

    const columns = [
        {
            title: 'Nombre / Participante',
            dataIndex: 'participant_name',
            key: 'participant_name',
            render: (text) => text || <Text type="secondary">Nuevo Prospecto</Text>
        },
        {
            title: 'Teléfono',
            dataIndex: 'phone',
            key: 'phone',
        },
        {
            title: 'Estado',
            dataIndex: 'status',
            key: 'status',
            render: (status) => {
                let color = 'blue';
                if (status === 'contacted') color = 'green';
                if (status === 'failed') color = 'red';
                if (status === 'rescheduled') color = 'orange';
                return <Tag color={color}>{status.toUpperCase()}</Tag>;
            }
        }
    ];

    const tabItems = [
        {
            key: 'list',
            label: (
                <span>
                    <InfoCircleOutlined /> Información
                </span>
            ),
            children: (
                <div style={{ marginTop: 16 }}>
                    {groupedParticipants.length === 0 ? (
                        <Empty description="No hay contactos asignados" />
                    ) : (
                        <>
                            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                                <Button
                                    type="primary"
                                    ghost
                                    icon={<CloudDownloadOutlined />}
                                    onClick={() => handleExportBatch('')}
                                >
                                    Exportar Todo lo Cargado
                                </Button>
                            </div>
                            <Collapse accordion ghost>
                                {groupedParticipants.map((group) => (
                                    <Panel
                                        header={
                                            <div style={{ display: 'flex', justifyContent: 'space-between', width: '95%', alignItems: 'center' }}>
                                                <Space>
                                                    <Text strong>{group.tag}</Text>
                                                    <Tag>{group.count} registros</Tag>
                                                </Space>
                                                <Button
                                                    size="small"
                                                    icon={<CloudDownloadOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleExportBatch(group.tag);
                                                    }}
                                                >
                                                    Exportar
                                                </Button>
                                            </div>
                                        }
                                        key={group.tag}
                                    >
                                        <Table
                                            dataSource={group.participants}
                                            columns={columns}
                                            size="small"
                                            pagination={{ pageSize: 5 }}
                                            rowKey="id"
                                        />
                                    </Panel>
                                ))}
                            </Collapse>
                        </>
                    )}
                </div>
            )
        },
        ...(campaign?.origen_data !== 'agent' ? [{
            key: 'assign',
            label: (
                <span>
                    <PlusOutlined /> Carga Masiva
                </span>
            ),
            children: (
                <div style={{ marginTop: 16 }}>
                    <Form form={form} layout="vertical">
                        <Form.Item
                            label="Etiqueta de Lote (Batch Tag)"
                            name="batch_tag"
                            rules={[{ required: true, message: 'La etiqueta es obligatoria' }]}
                            tooltip="Ej: Donaciones_Enero_2025. Ayuda a filtrar y exportar posteriormente."
                        >
                            <Input placeholder="Ej: Lote_Verano_01" />
                        </Form.Item>

                        <Divider plain>Método de Carga</Divider>

                        <Tabs
                            type="card"
                            size="small"
                            activeKey={loadMode}
                            onChange={setLoadMode}
                            items={[
                                {
                                    key: 'csv',
                                    label: 'Archivo (Excel/CSV)',
                                    children: (
                                        <div style={{ paddingTop: 8 }}>
                                            <Dragger
                                                beforeUpload={handleFileLoad}
                                                accept=".csv,.txt,.xlsx,.xls"
                                                maxCount={1}
                                            >
                                                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                <p className="ant-upload-text">Arrastre archivo (Excel .xlsx, .xls o CSV)</p>
                                            </Dragger>
                                            {csvPhones.length > 0 && <Text type="success">{csvPhones.length} números detectados.</Text>}
                                        </div>
                                    )
                                },
                                {
                                    key: 'manual',
                                    label: 'Directo',
                                    children: (
                                        <Form.Item name="manual_phones" style={{ paddingTop: 8 }}>
                                            <TextArea rows={6} placeholder="Pegue aquí los números..." />
                                        </Form.Item>
                                    )
                                }
                            ]}
                        />

                        <div style={{ marginTop: 24, textAlign: 'right' }}>
                            <Button type="primary" onClick={handleSubmitLoad} loading={loading} block>
                                Iniciar Carga de Contactos
                            </Button>
                        </div>
                    </Form>
                </div>
            )
        }] : [])
    ];

    return (
        <Drawer
            title={
                <Space>
                    <SearchOutlined />
                    <span>Contactos: {campaign?.name}</span>
                </Space>
            }
            width={600}
            onClose={onClose}
            open={visible}
            loading={loading && activeTab === 'list'}
        >
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
            />
        </Drawer>
    );
};

export default CampaignContactsDrawer;
